<template>
	<div
		class="device-list"
		:class="{ mobile: inMobileApp, desktop: !inMobileApp }"
	>
		<device-list-mobile
			v-if="inMobileApp"
			v-bind="bindings"
			:show-nested="showNested"
			:show-delete-room-snackbar.sync="showDeleteRoomSnackbar"
		/>
		<device-list-desktop
			v-else
			v-bind="bindings"
			:show-nested="showNested"
			:show-delete-room-snackbar.sync="showDeleteRoomSnackbar"
			@loaded="selectFirstChild"
		/>

		<!-- Dialogs -->
		<device-dialog-remove ref="removeDialog" @error="handleError" />
		<device-dialog-rename ref="renameDialog" />
		<device-dialog-move ref="moveDialog" :rooms="rooms" />

		<!-- Snackbars -->
		<nav-snackbar
			v-model="showError"
			color="error"
			text-color="white"
			:timeout="6000"
			multi-line
			top
			>{{ error }}</nav-snackbar
		>
		<nav-snackbar v-model="showDeleteRoomSnackbar" top>{{
			strings.devices.removeRoomNotEmpty
		}}</nav-snackbar>
	</div>
</template>

<style lang="scss">
// Lower opacity on disabled device list items' icon
.v-list-item--disabled .v-list-item__icon .v-icon {
	opacity: 0.4;
}
</style>

<script>
import _ from "lodash";
import { mapGetters, mapActions } from "vuex";

import { NodeOperationError, DEVICE_CLASS_CONTROLLER } from "@/utils/k4util";
import strings from "@/utils/strings";

export default {
	props: {
		fnFilterItems: { type: Function, default: _.identity },
		fnClickItem: { type: Function, required: true },

		showNested: { type: Boolean, required: false, default: false },
		provisioning: { type: Boolean, required: false, default: true },

		disable: Boolean,
	},

	data: () => ({
		strings,
		error: null,
		showDeleteRoomSnackbar: false,
	}),

	computed: {
		...mapGetters({
			inMobileApp: "config/inMobileApp",
			nodeByGuid: "controller/nodes/nodeByGuid",

			roomsLive: "controller/nodes/rooms",
			roomsLocal: "devices/rooms",
		}),

		isLocal() {
			return !!this.$store.state.controller.local;
		},

		rooms() {
			return this.isLocal ? this.roomsLocal : this.roomsLive;
		},

		nodes() {
			const nodes = _.flatMap(this.rooms, (room) => [
				room,
				...this.fnFilterItems(room.children),
			]);
			return nodes;
		},

		firstChild() {
			return this.nodes && this.nodes[0];
		},

		bindings() {
			return {
				nodes: this.nodes,

				fnRemoveItem: this.popRemoveDialog,
				fnRenameItem: this.popRenameDialog,
				fnMoveItem: this.popMoveDialog,

				fnClickItem: this.fnClickItem,

				disable: this.disable,
			};
		},

		showError: {
			get() {
				return !_.isNil(this.error);
			},
			set(val) {
				if (!val) {
					this.error = null;
				}
			},
		},
	},

	methods: {
		...mapActions({
			remove: "controller/nodes/remove",
			rename: "controller/nodes/rename",
			move: "controller/nodes/move",
		}),

		popRemoveDialog(item) {
			return this.$refs.removeDialog.pop(item).then((value) => {
				if (value) return this.remove({ node: value });
			});
		},

		popRenameDialog(item) {
			this.$refs.renameDialog.pop(item, this.rename);
		},

		popMoveDialog(item) {
			this.$refs.moveDialog.pop(item, this.move);
		},

		handleError(error) {
			if (error instanceof NodeOperationError) {
				this.error = error.message;
			}
		},

		isController(item) {
			const cls = item.meta ? item.meta.cls : item.cls;
			return cls === DEVICE_CLASS_CONTROLLER;
		},

		selectFirstChild() {
			if (this.firstChild && !this.$vuetify.breakpoint.smAndDown) {
				this.fnClickItem(this.firstChild.children[0]);
			}
		},
	},
};
</script>
